import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PhotoUploader from "@/components/PhotoUploader";
import GiveLeviProjectInformation from "@/components/GiveLeviProjectInformation";
import VideoWalkHistory from "@/components/videoWalk/VideoWalkHistory";
import { ProjectLifecycleTimeline } from "@/components/WorkflowSteps";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FileText, Send, FileSignature, Receipt, MessageSquare, Sparkles, ClipboardList, ShieldCheck, Loader2, Pencil, X, Save, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import WorkAreas from "@/components/workarea/WorkAreas";
import ProjectScheduleCard from "@/components/schedule/ProjectScheduleCard";
import MaterialTakeoffCard from "@/components/takeoffs/MaterialTakeoffCard";
import ProfitabilityCard from "@/components/profitability/ProfitabilityCard";
import ProjectCloseoutSection from "@/components/ProjectCloseoutSection";
import LeviProjectSnapshot from "@/components/levi/LeviProjectSnapshot";
import StatusPill from "@/components/StatusPill";
import SmsPermissionCard from "@/components/sms/SmsPermissionCard";

const ProjectDetail = () => {
  const { t } = useLang();
  const { pid } = useParams();
  // Phase 5A · Publish current-page workspace hint. Backend
  // re-verifies the ID against the authenticated tenant before use.
  useLeviWorkspaceHint("project_detail", {
    active_project_id: pid,
    open_document_type: "project",
    open_document_id: pid,
    current_section: "project_detail",
  });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isResume = params.get("resume") === "1";
  // Vision Conversational Deployment · Checkpoint 1 (PO 2026-08-05).
  // When Levi returns ``action_type=open_camera``, ``useLeviCommand``
  // navigates to ``/app/projects/{pid}?levi_vision=1``. We reveal the
  // existing VideoWalkHistory recorder (prepared, not recording) and
  // scroll it into view. Flag-gated at the frontend edge; when OFF,
  // this behaves as before.
  const _vcEnabledFrontend = (
    String(process.env.REACT_APP_LEVI_VISION_CONVERSATIONAL_ENABLED || "")
      .toLowerCase() === "1"
    || String(process.env.REACT_APP_LEVI_VISION_CONVERSATIONAL_ENABLED || "")
      .toLowerCase() === "true"
  );
  const _leviVisionRequested = _vcEnabledFrontend
    && params.get("levi_vision") === "1";
  const _videoWalkAnchorRef = useRef(null);
  useEffect(() => {
    if (!_leviVisionRequested) return;
    // Wait a paint tick so the section is mounted before scrolling.
    const t = setTimeout(() => {
      try {
        _videoWalkAnchorRef.current?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      } catch { /* noop */ }
    }, 150);
    return () => clearTimeout(t);
  }, [_leviVisionRequested]);
  const [project, setProject] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [closeoutPackets, setCloseoutPackets] = useState([]);
  const [finishing, setFinishing] = useState(false);
  const [generatingCloseout, setGeneratingCloseout] = useState(false);

  // Rio dry-run finding #8 (iter33) — post-intake edit for job walk narrative.
  // Editable fields: job_description, voice_transcript, typed_notes,
  // measurements, material_notes, labor_notes, special_conditions,
  // customer_concerns. Save uses the existing PUT /api/projects/{pid}
  // endpoint which writes only to the ``projects`` collection — never
  // touches estimates, line_items, proposals, contracts, invoices,
  // customer_updates, voice_notes, work_orders, or closeout_packets.
  // Contractor must manually re-run "Organize Scope with AI" if they
  // want the changed notes reflected in estimate line items.
  const INTAKE_EDITABLE_FIELDS = [
    "job_description", "voice_transcript", "typed_notes", "measurements",
    "material_notes", "labor_notes", "special_conditions", "customer_concerns",
  ];
  const [editingIntake, setEditingIntake] = useState(false);
  const [intakeDraft, setIntakeDraft] = useState({});
  const [savingIntake, setSavingIntake] = useState(false);
  const [intakeJustSaved, setIntakeJustSaved] = useState(false);

  const startIntakeEdit = () => {
    // Slice 2J correction · Job Notes & Measurements combined field.
    // On enter-edit we merge any legacy `measurements` value into
    // `typed_notes` so the contractor sees ONE combined field. The
    // legacy value is never discarded — the entire content lives in
    // typed_notes on save. `measurements` is written as "" so the
    // backend field remains present and non-competing on the next
    // read. If `typed_notes` already contains the measurements
    // substring, we drop the duplicate silently (never concatenate
    // twice).
    const draft = {};
    INTAKE_EDITABLE_FIELDS.forEach((k) => { draft[k] = project?.[k] || ""; });
    const rawM = (draft.measurements || "").trim();
    const rawN = (draft.typed_notes || "").trim();
    if (rawM && !rawN.includes(rawM)) {
      draft.typed_notes = rawN ? `${rawM}\n\n${rawN}` : rawM;
    }
    draft.measurements = "";
    setIntakeDraft(draft);
    setIntakeJustSaved(false);
    setEditingIntake(true);
  };
  const cancelIntakeEdit = () => {
    setEditingIntake(false);
    setIntakeDraft({});
  };
  const updateIntakeDraft = (k) => (e) => setIntakeDraft({ ...intakeDraft, [k]: e.target.value });
  const saveIntakeEdit = async () => {
    setSavingIntake(true);
    try {
      // Merge draft into full project body so PUT /projects preserves
      // customer identity + Slice E intake fields untouched.
      const body = { ...project, ...intakeDraft };
      const { data } = await api.put(`/projects/${pid}`, body);
      setProject(data);
      setEditingIntake(false);
      setIntakeJustSaved(true);
      toast.success(t("project_detail.toast_notes_saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("project_detail.toast_notes_failed"));
    } finally {
      setSavingIntake(false);
    }
  };

  useEffect(() => {
    api.get(`/projects/${pid}`).then((r) => setProject(r.data));
    Promise.all([
      api.get("/estimates"), api.get("/proposals"), api.get("/contracts"),
      api.get("/invoices"), api.get("/customer-updates"), api.get("/work-orders"),
      api.get("/closeout-packets"),
    ]).then(([e, p, c, i, u, w, cp]) => {
      setEstimates(e.data.filter((x) => x.project_id === pid));
      setProposals(p.data.filter((x) => x.project_id === pid));
      setContracts(c.data.filter((x) => x.project_id === pid));
      setInvoices(i.data.filter((x) => x.project_id === pid));
      setUpdates(u.data.filter((x) => x.project_id === pid));
      setWorkOrders(w.data.filter((x) => x.project_id === pid));
      setCloseoutPackets(cp.data.filter((x) => x.project_id === pid));
    });
  }, [pid]);

  if (!project) return <DashboardLayout title={t("project_detail.fallback_title")} subtitle={t("project_detail.loading")}><div /></DashboardLayout>;

  const isDraft = project.status === "draft";

  // Slice 2D4 Batch 2 cleanup · Localize known status enum values when
  // rendered in the sidebar Related Lists. Unknown enum values fall
  // back to the raw stored string so DB integrity is preserved.
  const localizeStatus = (s) => {
    if (!s) return s;
    const key = `project_detail.status_labels.${s}`;
    const val = t(key);
    return val === key ? s : val;
  };

  const finishDraft = async () => {
    setFinishing(true);
    try {
      await api.put(`/projects/${pid}`, { ...project, status: "active" });
      // Find or create an estimate against this project
      let est = estimates[0];
      if (!est) {
        const { data } = await api.post("/estimates", {
          project_id: pid, line_items: [], tax_rate: 8.0, deposit_pct: 50.0,
        });
        est = data;
      }
      toast.success(t("project_detail.toast_draft_finished"));
      navigate(`/app/estimates/${est.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("project_detail.toast_draft_failed"));
    } finally {
      setFinishing(false);
    }
  };

  const generateCloseoutPacket = async () => {
    setGeneratingCloseout(true);
    try {
      const { data } = await api.post("/closeout-packets/generate", { project_id: pid });
      const existing = closeoutPackets[0];
      toast.success(existing ? t("project_detail.toast_closeout_existing") : t("project_detail.toast_closeout_generated"));
      navigate(`/app/closeout/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("project_detail.toast_closeout_failed"));
    } finally {
      setGeneratingCloseout(false);
    }
  };


  return (
    <DashboardLayout
      title={project.customer_name}
      subtitle={`${project.job_type || t("project_detail.fallback_title")} · ${project.job_address || t("project_detail.no_address")}`}
      actions={
        <Link to="/app/projects" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]">
          <ArrowLeft size={14} /> {t("project_detail.all_projects")}
        </Link>
      }
    >
      {/* Slice 2T · Levi 1.0 Project Snapshot — mounted at the top of
          the Project Workspace, directly below the project header and
          above the draft banner. Zero fetches, zero mutations, zero LLM
          calls. Consumes ONLY data already loaded by this page. */}
      <LeviProjectSnapshot
        project={project}
        estimates={estimates}
        proposals={proposals}
        contracts={contracts}
        invoices={invoices}
        workOrders={workOrders}
        closeoutPackets={closeoutPackets}
        handlers={{
          open_intake_edit_panel: startIntakeEdit,
          generate_closeout_packet: generateCloseoutPacket,
        }}
      />
      {/* V-P1 · Project Connected View — Working Docs · Job Intelligence · Job Outcome.
          Summary strip composed strictly from existing project data
          already loaded on this page. Deep-links to the underlying
          detail sections rendered below (jump anchors #vp1-working-docs,
          #vp1-job-intelligence, #vp1-job-outcome). No new lifecycle
          states, no new schemas, no redesign of project architecture. */}
      <ProjectConnectedOverview
        project={project}
        estimates={estimates}
        proposals={proposals}
        contracts={contracts}
        invoices={invoices}
        workOrders={workOrders}
        closeoutPackets={closeoutPackets}
        t={t}
      />
      {isDraft && (
        <div className="jc-card p-5 mb-6 border-l-4 border-l-[var(--jc-yellow)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="draft-banner">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-[var(--jc-yellow)] mt-0.5" />
            <div>
              <div className="font-display text-lg">{isResume ? t("project_detail.resuming_draft") : t("project_detail.still_draft")}</div>
              <p className="text-sm text-[var(--jc-text-soft)] mt-1">
                {t("project_detail.draft_body")}
              </p>
            </div>
          </div>
          <button
            onClick={finishDraft}
            disabled={finishing}
            data-testid="finish-draft-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60 shrink-0"
          >
            {finishing ? t("project_detail.finish_working") : t("project_detail.finish_action")}
          </button>
        </div>
      )}
      {/* Slice 2H · Measurement Intelligence Foundation — Work Areas
          card. Universal manual Work Area capture, review, approval,
          and project storage. Never mutates estimates, proposals,
          contracts, invoices, pricing, or lifecycle status. */}
      <WorkAreas projectId={project.id} />
      <ProjectScheduleCard projectId={project.id} />
      <MaterialTakeoffCard projectId={project.id} />
      {/* V-P1 anchor: Job Outcome column deep-links here. */}
      <div id="vp1-job-outcome" />
      <div className="mt-8 mb-3 flex items-center justify-between" data-testid="vp1-section-job-outcome-header">
        <div>
          <div className="overline">Job outcome</div>
          <h2 className="font-display text-xl mt-1">Profitability · Closeout · Warranty</h2>
        </div>
      </div>
      <ProfitabilityCard projectId={project.id} />
      <ProjectLifecycleTimeline
        projectId={project.id}
        estimates={estimates}
        proposals={proposals}
        contracts={contracts}
        invoices={invoices}
        updates={updates}
        workOrders={workOrders}
        closeoutPackets={closeoutPackets}
      />

      {!isDraft && (
        <ProjectCloseoutSection
          project={project}
          closeoutPackets={closeoutPackets}
          invoices={invoices}
          workOrders={workOrders}
          onProjectRefresh={() => api.get(`/projects/${pid}`).then((r) => setProject(r.data))}
        />
      )}

      {!isDraft && (
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          <Link
            to={`/app/projects/${pid}/crew-packet`}
            data-testid="project-crew-packet-action"
            className="jc-card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:border-[var(--jc-blue)]/60 hover:shadow-sm transition"
          >
            <div className="flex items-start gap-3 min-w-0">
              <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--jc-navy)]/5 text-[var(--jc-navy)] flex items-center justify-center">
                <ClipboardList size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--jc-text)]">{t("project_detail.crew_packet_title")}</div>
                <div className="text-xs text-[var(--jc-text-soft)]">
                  {t("project_detail.crew_packet_body_live")}
                </div>
              </div>
            </div>
            <span
              data-testid="open-crew-packet-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary"
            >
              <ClipboardList size={14} /> {t("project_detail.crew_packet_open")}
            </span>
          </Link>

          <div className="jc-card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="project-closeout-action">
            <div className="flex items-start gap-3 min-w-0">
              <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--jc-teal)]/10 text-[var(--jc-teal)] flex items-center justify-center">
                <ShieldCheck size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--jc-text)]">{t("project_detail.closeout_title")}</div>
                <div className="text-xs text-[var(--jc-text-soft)]">
                  {closeoutPackets.length > 0
                    ? t("project_detail.closeout_body_existing")
                    : t("project_detail.closeout_body_idle")}
                </div>
              </div>
            </div>
            <button
              onClick={generateCloseoutPacket}
              disabled={generatingCloseout}
              data-testid="generate-closeout-packet-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-teal disabled:opacity-60"
            >
              {generatingCloseout ? <><Loader2 className="animate-spin" size={14} /> {t("project_detail.closeout_working")}</> : <><ShieldCheck size={14} /> {closeoutPackets.length > 0 ? t("project_detail.closeout_open") : t("project_detail.closeout_generate")}</>}
            </button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="jc-card p-6" data-testid="project-job-details-section">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="overline">{t("project_detail.job_details")}</div>
              {!isDraft && !editingIntake && (
                <button
                  onClick={startIntakeEdit}
                  data-testid="project-edit-intake-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                  title={t("project_detail.edit_notes_title")}
                >
                  <Pencil size={12} /> {t("project_detail.edit_notes")}
                </button>
              )}
            </div>
            {intakeJustSaved && !editingIntake && (
              <div
                data-testid="project-intake-saved-banner"
                className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-900 flex items-start gap-2"
              >
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-700" />
                <span>
                  Job walk notes saved. Line items are unchanged — re-run <b>&ldquo;Organize Scope with AI&rdquo;</b> on the estimate if you want the changes reflected there.
                </span>
              </div>
            )}
            <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div><dt className="text-[var(--jc-text-soft)]">{t("project_detail.label_customer_phone")}</dt><dd>{project.customer_phone || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("project_detail.label_customer_email")}</dt><dd>{project.customer_email || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("project_detail.label_timeline")}</dt><dd>{project.timeline || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("project_detail.label_created")}</dt><dd>{formatDate(project.created_at)}</dd></div>
            </dl>

            {/* LV-02B Phase 4 · Workstream C — transactional SMS delivery permission. */}
            <SmsPermissionCard projectId={project.id} />

            {editingIntake ? (
              <div className="mt-5 space-y-3" data-testid="project-intake-edit-form">
                <p className="text-xs text-[var(--jc-text-soft)]">
                  Edits update your project / job walk notes only. Estimate line items, proposals, contracts, invoices, and PDFs are <b>not</b> auto-rewritten — re-run &ldquo;Organize Scope with AI&rdquo; on the estimate if you want those changes to feed the line items.
                </p>
                <IntakeField label="Short project summary" name="job_description" rows={2} value={intakeDraft.job_description} onChange={updateIntakeDraft("job_description")} placeholder='One-line summary the customer will recognize.' />
                <IntakeField label="Voice transcript" name="voice_transcript" rows={5} value={intakeDraft.voice_transcript} onChange={updateIntakeDraft("voice_transcript")} placeholder="Full transcribed walk-through." />
                {/* Slice 2J correction · One combined Job Notes & Measurements field. */}
                <IntakeField
                  label="Job notes & measurements"
                  name="typed_notes"
                  rows={6}
                  value={intakeDraft.typed_notes}
                  onChange={updateIntakeDraft("typed_notes")}
                  placeholder="Add measurements, square footage, dimensions, scope details, materials, access issues, customer requests, special conditions, or anything else that matters for this job."
                />
                <div className="grid md:grid-cols-2 gap-3">
                  <IntakeField label="Material notes" name="material_notes" rows={3} value={intakeDraft.material_notes} onChange={updateIntakeDraft("material_notes")} />
                  <IntakeField label="Labor notes" name="labor_notes" rows={3} value={intakeDraft.labor_notes} onChange={updateIntakeDraft("labor_notes")} />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <IntakeField label="Special conditions" name="special_conditions" rows={2} value={intakeDraft.special_conditions} onChange={updateIntakeDraft("special_conditions")} />
                  <IntakeField label="Customer concerns" name="customer_concerns" rows={2} value={intakeDraft.customer_concerns} onChange={updateIntakeDraft("customer_concerns")} />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={saveIntakeEdit}
                    disabled={savingIntake}
                    data-testid="project-intake-save-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                  >
                    {savingIntake ? <><Loader2 className="animate-spin" size={14} /> Saving...</> : <><Save size={14} /> Save job walk notes</>}
                  </button>
                  <button
                    onClick={cancelIntakeEdit}
                    disabled={savingIntake}
                    data-testid="project-intake-cancel-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
                  >
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {project.job_description && (
                  <div className="mt-4 text-sm">
                    <div className="text-[var(--jc-text-soft)] mb-1">{t("project_detail.label_description")}</div>
                    <p>{project.job_description}</p>
                  </div>
                )}
                {project.voice_transcript && (
                  <div className="mt-4 text-sm">
                    <div className="text-[var(--jc-text-soft)] mb-1">Voice transcript</div>
                    <p className="whitespace-pre-wrap">{project.voice_transcript}</p>
                  </div>
                )}
              </>
            )}
          </section>

          <JobIntakeCard project={project} />

          {String(process.env.REACT_APP_LEVI_LPKI_CONTRACTOR_INTAKE_ENABLED || "").toLowerCase() === "1"
            || String(process.env.REACT_APP_LEVI_LPKI_CONTRACTOR_INTAKE_ENABLED || "").toLowerCase() === "true" ? (
            <GiveLeviProjectInformation projectId={project.id} />
          ) : null}

          <section className="jc-card p-6">
            <div className="overline mb-3">{t("project_detail.photos_docs")}</div>
            <PhotoUploader projectId={project.id} />
          </section>

          <div ref={_videoWalkAnchorRef}
               style={{ scrollMarginTop: 80 }}
               data-testid="levi-vision-anchor" />
          {/* V-P1 anchor: Job Intelligence column deep-links here. */}
          <div id="vp1-job-intelligence" />
          <div className="flex items-center justify-between mb-3" data-testid="vp1-section-job-intelligence-header">
            <div>
              <div className="overline">Job intelligence</div>
              <h2 className="font-display text-xl mt-1">Vision walks · Photos · Files</h2>
            </div>
          </div>
          <VideoWalkHistory
            projectId={project.id}
            initialOpen={_leviVisionRequested}
            autoStart={_leviVisionRequested}
          />

          {/* V-P1 anchor: Working Docs column deep-links here. */}
          <div id="vp1-working-docs" />
          <div className="flex items-center justify-between mb-3 mt-4" data-testid="vp1-section-working-docs-header">
            <div>
              <div className="overline">Working docs</div>
              <h2 className="font-display text-xl mt-1">Estimates · Proposals · Contracts · Invoices</h2>
            </div>
          </div>
          <RelatedList
            title={t("project_detail.related_estimates")} icon={FileText} items={estimates} to="/app/estimates"
            create={`/app/estimates/new?project=${pid}`}
            renderLine={(e) => `${e.line_items?.length || 0} line items · ${localizeStatus(e.status)}`}
          />
          <RelatedList title={t("project_detail.related_proposals")} icon={Send} items={proposals} to="/app/proposals" renderLine={(x) => localizeStatus(x.status)} />
          <RelatedList title={t("project_detail.related_contracts")} icon={FileSignature} items={contracts} to="/app/contracts" renderLine={(x) => localizeStatus(x.status)} />
          <ChangeOrdersRelatedCard projectId={pid} />
          <RelatedList title={t("project_detail.related_invoices")} icon={Receipt} items={invoices} to="/app/invoices" renderLine={(x) => `${x.invoice_type} · ${x.payment_status}`} />
          <RelatedList title={t("project_detail.related_updates")} icon={MessageSquare} items={updates} to="/app/customer-updates" renderLine={(x) => `${x.update_type} (${x.language.toUpperCase()})`} />
        </div>

        <aside className="space-y-4">
          <div className="jc-card p-6">
            <div className="overline mb-2">{t("project_detail.customer")}</div>
            <div className="font-display text-xl">{project.customer_name}</div>
            <div className="text-sm text-[var(--jc-text-soft)] mt-1">{project.customer_email}</div>
            <div className="text-sm text-[var(--jc-text-soft)]">{project.customer_phone}</div>
          </div>
          <div className="jc-card p-6">
            <div className="overline mb-2">{t("project_detail.notes")}</div>
            <div className="text-sm space-y-2">
              {/* Slice 2J correction · Job notes & measurements is one
                  contractor-facing concept. If legacy `measurements`
                  data still exists on the project (pre-correction),
                  join it with `typed_notes` for display so nothing is
                  hidden from the contractor. */}
              <div>
                <span className="text-[var(--jc-text-soft)]">{t("project_detail.job_notes_prefix")}</span>{" "}
                {(() => {
                  const rawM = (project.measurements || "").trim();
                  const rawN = (project.typed_notes || "").trim();
                  if (!rawM && !rawN) return "—";
                  if (!rawM) return rawN;
                  if (!rawN) return rawM;
                  if (rawN.includes(rawM)) return rawN;
                  return `${rawM}\n\n${rawN}`;
                })()}
              </div>
              <div><span className="text-[var(--jc-text-soft)]">{t("project_detail.material_prefix")}</span> {project.material_notes || "—"}</div>
              <div><span className="text-[var(--jc-text-soft)]">{t("project_detail.labor_prefix")}</span> {project.labor_notes || "—"}</div>
              <div><span className="text-[var(--jc-text-soft)]">{t("project_detail.special_prefix")}</span> {project.special_conditions || "—"}</div>
              <div><span className="text-[var(--jc-text-soft)]">{t("project_detail.concerns_prefix")}</span> {project.customer_concerns || "—"}</div>
            </div>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
};

// V-P1 · Project Connected View — three-column overview strip.
// Working Docs · Job Intelligence · Job Outcome. Composed strictly
// from existing project-loaded data; deep-links jump to the same
// underlying sections rendered further down the page (#anchors).
// STRICT: no new lifecycle states, no new scoring, no new schemas.
const ProjectConnectedOverview = ({
  project, estimates, proposals, contracts, invoices, workOrders, closeoutPackets, t,
}) => {
  const estCount = (estimates || []).length;
  const propCount = (proposals || []).length;
  const contractCount = (contracts || []).length;
  const invoiceCount = (invoices || []).length;
  const openInvoiceCount = (invoices || []).filter((i) => i.payment_status !== "paid").length;
  const paidInvoiceCount = (invoices || []).filter((i) => i.payment_status === "paid").length;
  const woCount = (workOrders || []).length;
  const packetCount = (closeoutPackets || []).length;

  const dominantEstimate = (estimates || [])[0];
  const dominantContract = (contracts || [])[0];
  const dominantInvoice = (invoices || [])[0];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const Column = ({ testid, overline, headline, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="jc-card p-4 text-left w-full jc-card-hover group flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <span className="overline text-[10px]">{overline}</span>
        <ArrowLeft
          size={12}
          className="rotate-180 text-[var(--jc-text-soft)] group-hover:text-[var(--jc-blue)] group-hover:translate-x-0.5 transition"
        />
      </div>
      <div className="font-display text-lg leading-tight">{headline}</div>
      <div className="flex flex-wrap gap-1.5 mt-1">{children}</div>
    </button>
  );

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6"
      data-testid="project-connected-overview"
    >
      <Column
        testid="pcv-column-working-docs"
        overline={t("project_detail.pcv_working_docs_overline") || "Working Docs"}
        headline={t("project_detail.pcv_working_docs_headline") || "Estimates · Proposals · Contracts"}
        onClick={() => scrollTo("vp1-working-docs")}
      >
        {estCount > 0 && (
          <span className="jc-pill jc-pill-neutral"><span className="jc-pill-dot" />{estCount} Est</span>
        )}
        {propCount > 0 && (
          <span className="jc-pill jc-pill-neutral"><span className="jc-pill-dot" />{propCount} Prop</span>
        )}
        {contractCount > 0 && (
          <span className="jc-pill jc-pill-neutral"><span className="jc-pill-dot" />{contractCount} Ctr</span>
        )}
        {dominantContract && (
          <StatusPill status={dominantContract.status} kind="contract" t={t} />
        )}
        {dominantEstimate && !dominantContract && (
          <StatusPill status={dominantEstimate.status} kind="estimate" t={t} />
        )}
      </Column>

      <Column
        testid="pcv-column-job-intelligence"
        overline={t("project_detail.pcv_job_intel_overline") || "Job Intelligence"}
        headline={t("project_detail.pcv_job_intel_headline") || "Vision · Measurements · Levi notes"}
        onClick={() => scrollTo("vp1-job-intelligence")}
      >
        {woCount > 0 && (
          <span className="jc-pill jc-pill-neutral"><span className="jc-pill-dot" />{woCount} Crew</span>
        )}
        <span className="jc-pill jc-pill-approved">
          <span className="jc-pill-dot" />{t("project_detail.pcv_pill_ask_levi") || "Ask Levi"}
        </span>
      </Column>

      <Column
        testid="pcv-column-job-outcome"
        overline={t("project_detail.pcv_job_outcome_overline") || "Job Outcome"}
        headline={t("project_detail.pcv_job_outcome_headline") || "Invoices · Profitability · Closeout"}
        onClick={() => scrollTo("vp1-job-outcome")}
      >
        {openInvoiceCount > 0 && (
          <span className="jc-pill jc-pill-unpaid"><span className="jc-pill-dot" />{openInvoiceCount} open</span>
        )}
        {paidInvoiceCount > 0 && (
          <span className="jc-pill jc-pill-paid"><span className="jc-pill-dot" />{paidInvoiceCount} paid</span>
        )}
        {packetCount > 0 && (
          <span className="jc-pill jc-pill-signed"><span className="jc-pill-dot" />{packetCount} pkt</span>
        )}
        {dominantInvoice && !openInvoiceCount && !paidInvoiceCount && (
          <StatusPill status={dominantInvoice.payment_status || dominantInvoice.status} kind="invoice" t={t} />
        )}
      </Column>
    </div>
  );
};

const IntakeField = ({ label, name, rows = 3, value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{label}</label>
    <textarea
      data-testid={`project-edit-${name}`}
      rows={rows}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
    />
  </div>
);

const RelatedList = ({ title, icon: Icon, items, to, renderLine, create }) => {
  const { t } = useLang();
  return (
  <section className="jc-card p-6">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-[var(--jc-text-soft)]" />
        <div className="overline">{title}</div>
      </div>
      {create && (
        <Link to={create} className="text-xs font-semibold text-[var(--jc-blue)]">{t("project_detail.related_new")}</Link>
      )}
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-[var(--jc-text-soft)]">{t("project_detail.related_none_yet")}</div>
    ) : (
      <ul className="divide-y divide-[var(--jc-border)] text-sm">
        {items.map((i) => (
          <li key={i.id} className="py-2">
            <Link to={`${to}/${i.id}`} className="flex items-center justify-between hover:text-[var(--jc-blue)]">
              <span>{renderLine(i)}</span>
              <span className="text-xs text-[var(--jc-text-soft)]">{formatDate(i.created_at)}</span>
            </Link>
          </li>
        ))}
      </ul>
    )}
  </section>
  );
};

// Phase 9-B · PR-4B · Additive card that surfaces this project's
// change orders. Reuses the existing /api/change-orders endpoint —
// does NOT duplicate any calculation. When the feature is off (404
// from the API) it silently renders nothing.
const CHANGE_ORDER_STATUS_LABEL = {
  en: {
    draft: "Draft",
    contractor_approved: "Contractor-approved",
    canceled: "Canceled",
    superseded: "Superseded",
  },
  es: {
    draft: "Borrador",
    contractor_approved: "Aprobado por el contratista",
    canceled: "Cancelado",
    superseded: "Reemplazado",
  },
};
const ChangeOrdersRelatedCard = ({ projectId }) => {
  const { lang } = useLang();
  const [items, setItems] = useState(null);
  const [available, setAvailable] = useState(true);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/change-orders?project_id=${encodeURIComponent(projectId)}`)
      .then((r) => setItems(r.data.change_orders || []))
      .catch((e) => {
        if (e?.response?.status === 404) { setAvailable(false); return; }
        setItems([]);
      });
  }, [projectId]);
  if (!available) return null;
  const l = lang === "es" ? "es" : "en";
  const title = l === "es" ? "Órdenes de cambio" : "Change orders";
  const emptyText = l === "es" ? "Aún no hay órdenes de cambio." : "No change orders yet.";
  const newLabel = l === "es" ? "Abrir" : "Open";
  const to = `/app/projects/${projectId}/change-orders`;
  return (
    <section className="jc-card p-6" data-testid="project-change-orders-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-[var(--jc-text-soft)]" />
          <div className="overline">{title}</div>
        </div>
        <Link to={to} className="text-xs font-semibold text-[var(--jc-blue)]" data-testid="project-change-orders-open">
          {newLabel}
        </Link>
      </div>
      {items === null ? (
        <div className="text-sm text-[var(--jc-text-soft)]">…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="project-change-orders-empty">
          {emptyText}
        </div>
      ) : (
        <ul className="divide-y divide-[var(--jc-border)] text-sm">
          {items.slice(0, 5).map((co) => (
            <li key={co.id} className="py-2" data-testid={`project-co-row-${co.id}`}>
              <Link to={`${to}/${co.id}`} className="flex items-center justify-between hover:text-[var(--jc-blue)]">
                <span>#{co.change_order_number} · {(CHANGE_ORDER_STATUS_LABEL[l] || {})[co.status] || co.status}</span>
                <span className="text-xs text-[var(--jc-text-soft)]">{formatDate(co.updated_at)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};



const URGENCY_LABEL = {
  emergency: "🔴 Emergency",
  urgent: "🟠 Urgent",
  normal: "🟢 Normal",
  planned: "🔵 Planned",
};
const INSURANCE_LABEL = { yes: "Yes", no: "No", unknown: "Unknown" };

const JobIntakeCard = ({ project }) => {
  const {
    urgency, service_needed, intake_access_notes,
    insurance_claim, insurance_carrier, insurance_claim_number,
    preferred_appointment_at, lead_source,
  } = project || {};
  const hasAny =
    urgency || service_needed || intake_access_notes || insurance_claim ||
    insurance_carrier || insurance_claim_number || preferred_appointment_at || lead_source;
  if (!hasAny) return null;
  const fmtAppt = (v) => {
    if (!v) return "—";
    try {
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toLocaleString();
    } catch { return v; }
  };
  return (
    <section className="jc-card p-6" data-testid="job-intake-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Job Intake</div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--jc-text-soft)] bg-[var(--jc-surface)] px-2 py-0.5 rounded">
          Internal
        </span>
      </div>
      <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        {urgency && (
          <div data-testid="intake-view-urgency">
            <dt className="text-[var(--jc-text-soft)]">Urgency</dt>
            <dd className="font-semibold">{URGENCY_LABEL[urgency] || urgency}</dd>
          </div>
        )}
        {service_needed && (
          <div data-testid="intake-view-service-needed">
            <dt className="text-[var(--jc-text-soft)]">Service needed</dt>
            <dd>{service_needed}</dd>
          </div>
        )}
        {insurance_claim && (
          <div data-testid="intake-view-insurance-claim">
            <dt className="text-[var(--jc-text-soft)]">Insurance claim</dt>
            <dd>{INSURANCE_LABEL[insurance_claim] || insurance_claim}</dd>
          </div>
        )}
        {insurance_carrier && (
          <div data-testid="intake-view-insurance-carrier">
            <dt className="text-[var(--jc-text-soft)]">Insurance carrier</dt>
            <dd>{insurance_carrier}</dd>
          </div>
        )}
        {insurance_claim_number && (
          <div data-testid="intake-view-claim-number">
            <dt className="text-[var(--jc-text-soft)]">Claim number</dt>
            <dd className="font-mono text-xs">{insurance_claim_number}</dd>
          </div>
        )}
        {preferred_appointment_at && (
          <div data-testid="intake-view-preferred-appointment">
            <dt className="text-[var(--jc-text-soft)]">Preferred appointment</dt>
            <dd>{fmtAppt(preferred_appointment_at)}</dd>
          </div>
        )}
        {lead_source && (
          <div data-testid="intake-view-lead-source">
            <dt className="text-[var(--jc-text-soft)]">Lead source</dt>
            <dd>{lead_source}</dd>
          </div>
        )}
      </dl>
      {intake_access_notes && (
        <div className="mt-4 text-sm" data-testid="intake-view-access-notes">
          <div className="text-[var(--jc-text-soft)] mb-1">Access notes</div>
          <p className="whitespace-pre-wrap">{intake_access_notes}</p>
        </div>
      )}
      <p className="text-[11px] text-[var(--jc-text-soft)] mt-4">
        Job intake stays internal. It never appears on customer proposals, contracts, invoices, or PDFs.
      </p>
    </section>
  );
};

export default ProjectDetail;
